import React, { useEffect, useState } from "react"
import { Table } from "react-bootstrap"
import { useNavigate, useLocation } from "react-router"
import DeleteModal from "components/Common/DeleteModal"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import {
  deleteRole,
  fetchRoles,
  updateRoleStatus,
} from "store/actions/rolesAction"
import Switch from "components/Shared/Switch"
import Filters from "components/Common/Filters"
import {
  Badge,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap"
import Paginations from "components/Common/Paginations"
import { showAlert } from "store/reducers/alertReducer"
import Breadcrumbs from "components/Common/Breadcrumb"
import DownloadCSV from "components/Shared/DownloadCSV"
import { commonItemPerPage, pageSizes } from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import TableShimmer from "components/Shared/TableShimmer"
import formatDateTime from "components/Shared/formatDateTime"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import { useTranslation } from "react-i18next"

const Roles = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Roles List | Namq")

  const navigate = useNavigate()

  const dispatch = useDispatch()
  const {
    loading,
    isLoading,
    error,
    rolesList,
    isUpdating,
    totalCount,
    totalPages,
    currentPage,
  } = useSelector(state => state.Roles)

  const { userRole, accountType } = useSelector(state => state.Login)
  const { text, type } = useSelector(state => state.Alert)
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [itemsPerPage, setItemsPerPage] = useState(commonItemPerPage)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [isApiCalled, setIsApiCalled] = useState(false)

  useEffect(() => {
    if (accountType == "superadmin") {
      getRolesList()
    } else {
      navigate("/dashboard")
    }
  }, [])

  const getRolesList = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: 10,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
    }
    dispatch(fetchRoles(payload))
    setIsApiCalled(true)
  }

  const handleEnableRole = (id, status) => {
    const payload = {
      id,
      status: status === 1 ? "0" : "1", //deactive 1 active
    }
    dispatch(updateRoleStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
    setSelectedId(id)
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // for delete
    }
    dispatch(deleteRole({ ...payload, setShowDeleteModal }))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  // FILTERS
  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const fetchRolesApiCall = (page = 1) => {
    const { status, sortOrder } = selectedFilters

    const searchParams = new URLSearchParams()
    searchTerm && searchParams.set("search", searchTerm)
    status !== 2 && searchParams.set("status", status.toString())
    sortOrder !== 1 && searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    dispatch(fetchRoles(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/roles?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    fetchRolesApiCall(1)
  }

  const onPageChange = page => {
    fetchRolesApiCall(page)
  }

  const handleReset = () => {
    setItemsPerPage(commonItemPerPage)
    const payload = {
      perPage: commonItemPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(fetchRoles(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/roles?page=${1}`)
        }
      })
      .catch(error => {})
    setSelectedFilters({ status: 2, sortOrder: 1 })
    setSearchTerm("")
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 500)

  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  const handleExcelDownload = async () => {
    const { status } = selectedFilters

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "ROLE",
      exportType: "xlsx",
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Roles"))
    }
  }

  const exportToCSV = async () => {
    const { status } = selectedFilters

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "ROLE",
      exportType: "csv",
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Roles"))
    }
  }

  return (
    <>
      <div className="page-content roles_page ">
        <div className="container-fluid">
          <Breadcrumbs />
          <Row>
            <Col xl={12}>
              <Card>
                <DataExportDropdown
                  cardTitle={t("Roles List")}
                  isAddBtn={loading ? false : true}
                  addBtnText={t("Add Role")}
                  onAddBtnClick={() => navigate("/roles/add")}
                  isExportData={loading ? false : true}
                  onDownloadCSV={exportToCSV}
                  onDownloadExcel={handleExcelDownload}
                />
                <CardBody>
                  {/* {!loading && ( */}
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    isCustomPageSize={true}
                    pageSizes={pageSizes}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    searchBy={t("Name")}
                  />
                  {/* )} */}

                  <div className="">
                    {loading ? (
                      <TableShimmer row={7} col={5} />
                    ) : rolesList.length === 0 ? (
                      <p className="text-center my-2">{t("No roles found")}</p>
                    ) : (
                      <Table className="table mb-0" hover>
                        <thead className="table-light full_th">
                          <tr>
                            <th>{t("S. No.")}</th>
                            <th>{t("Name")}</th>
                            <th>{t("Permissions")}</th>
                            <th
                              style={{
                                minWidth: "fit-content",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {t("Assigned Users")}
                            </th>
                            <th>{t("Created")}</th>
                            <th>{t("Status")}</th>
                            <th>{t("Action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rolesList?.map((role, index) => (
                            <tr
                              key={role._id}
                              onClick={() =>
                                navigate(`/roles/role-details?id=${role._id}`)
                              }
                              className="cursor-pointer"
                            >
                              <td> {(currentPage - 1) * 10 + index + 1}</td>
                              <td>
                                {language === "sa"
                                  ? role.nameAr || "-"
                                  : role.name || "-"}
                              </td>
                              <td>
                                <ShowMoreButton
                                  permissionTitles={role?.permissions?.filter(
                                    permission =>
                                      permission.permissions.some(
                                        p => p.isEnabled
                                      )
                                  )}
                                  t={t}
                                  language={language}
                                />
                              </td>
                              <td>{role?.users?.length}</td>
                              <td>{formatDateTime(role.createdAt) || "-"}</td>
                              <td>
                                {role.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Active")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Deactive")}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <div className="gap-2 d-flex align-items-center">
                                  {/* {rolesPermissions.edit && ( */}
                                  <Switch
                                    isChecked={role.status == 1}
                                    onClickHandler={e => {
                                      {
                                        e.stopPropagation()
                                        handleEnableRole(role._id, role.status)
                                      }
                                    }}
                                    disabled={
                                      selectedId === role._id && isUpdating
                                    }
                                  />
                                  {/* //  )} */}
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      tag="a"
                                      className="card-drop"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      <i className="mdi mdi-dots-vertical font-size-18"></i>
                                    </DropdownToggle>
                                    <DropdownMenu className="dropdown-menu-end">
                                      {/* {rolesPermissions.edit && ( */}
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(`/roles/edit?id=${role._id}`)
                                        }}
                                      >
                                        <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                        {t("Edit")}
                                      </DropdownItem>
                                      {/* )} */}

                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/roles/role-details?id=${role._id}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                        {t("View")}
                                      </DropdownItem>

                                      {/* {rolesPermissions.delete && ( */}
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          setItemIdToDelete(role._id)
                                          setShowDeleteModal(true)
                                        }}
                                      >
                                        <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                        {t("Delete")}
                                      </DropdownItem>
                                      {/* )} */}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>

                  {!loading && (
                    <div className="mt-3">
                      <Paginations
                        data={rolesList}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        isLoading={isLoading}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Role"),
        })}
      />
    </>
  )
}

export default Roles

const ShowMoreButton = ({ permissionTitles, t, language }) => {
  const [showMore, setShowMore] = React.useState(false)

  const combinedTitles = permissionTitles
    .map((permission, index) => {
      const { title, titleAr } = permission
      return (
        <span key={index} className="text-capitalize">
          {language === "sa" ? titleAr || "-" : title || "-"}{" "}
          {/* {index !== permissionTitles.length - 1 ? ", " : ""} */}
        </span>
      )
    })
    .reduce((acc, curr) => acc + curr.props.children, "")

  const truncatedTitles = showMore
    ? combinedTitles
    : combinedTitles.length > 100
    ? `${combinedTitles.substring(0, 100)}`
    : combinedTitles

  return (
    <span>
      {truncatedTitles || "-"}
      {combinedTitles.length > 100 && (
        <>
          {!showMore && "..."}

          <button
            onClick={e => {
              e.stopPropagation()
              setShowMore(!showMore)
            }}
            className="mx-2"
          >
            {showMore ? t("Show less") : t("show more")}
          </button>
        </>
      )}
    </span>
  )
}
